import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import {
  NotebookText, Plus, X, Search, FolderClosed, Loader2, Check, Unlink, ExternalLink, User,
} from 'lucide-react';

// Linked Notes section for the task detail panel. Per-user visibility is
// enforced by the backend — this only ever receives notes the current user can
// access. Real-time: the parent bumps `event` on a `task_links_changed` WS
// message for this task, which triggers a refetch.
export default function TaskLinkedNotes({ taskId, event, onOpenNote, notify }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchLinks = useCallback(async () => {
    try {
      const { data } = await notesApi.get(`/notes/tasks/tasks/${taskId}/links`);
      setLinks(data.links || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { setLoading(true); fetchLinks(); }, [fetchLinks]);
  // Refetch whenever the parent signals a link change for this task.
  useEffect(() => { if (event) fetchLinks(); }, [event, fetchLinks]);

  const unlink = useCallback(async (linkId) => {
    const prev = links;
    setLinks((ls) => ls.filter((l) => l.id !== linkId)); // optimistic
    try {
      await notesApi.delete(`/notes/tasks/tasks/${taskId}/links/${linkId}`);
    } catch {
      setLinks(prev); // revert
      notify?.('Could not unlink the note. Please try again.');
    }
  }, [taskId, links, notify]);

  return (
    <section data-testid="linked-notes-section" className="space-y-2.5">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-[13px] font-semibold text-[var(--n-ink)]">
          <NotebookText size={15} className="text-[var(--n-accent)]" />
          Linked Notes
          {links.length > 0 && (
            <span className="rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--n-muted)]" data-testid="linked-notes-count-chip">{links.length}</span>
          )}
        </h3>
        <button type="button" data-testid="task-link-note-button" onClick={() => setPickerOpen(true)}
          className="n-focus inline-flex items-center gap-1 rounded-lg border border-[var(--n-border)] px-2 py-1 text-[12px] font-medium text-[var(--n-accent)] transition-colors hover:bg-[var(--n-accent-bg)]">
          <Plus size={13} /> Link a Note
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-1 text-[12px] text-[var(--n-muted)]"><Loader2 size={13} className="animate-spin" /> Loading…</div>
      ) : links.length === 0 ? (
        <p data-testid="linked-notes-empty" className="text-[12px] text-[var(--n-muted)]">No notes linked yet. Use <span className="font-medium text-[var(--n-ink-soft)]">Link a Note</span> to attach one.</p>
      ) : (
        <div className="flex gap-2.5 overflow-x-auto pb-1 n-scroll" data-testid="linked-notes-list">
          {links.map((l) => (
            <div key={l.id} data-testid="linked-note-card" data-note-id={l.note_id}
              className="group/lnk relative flex w-[228px] shrink-0 cursor-pointer flex-col gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas)] p-3 transition-colors hover:border-[var(--n-accent)]"
              role="button" tabIndex={0}
              onClick={() => onOpenNote?.(l.note_id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenNote?.(l.note_id); } }}>
              <div className="flex items-start gap-2">
                <NotebookText size={15} className="mt-0.5 shrink-0 text-[var(--n-accent)]" />
                <span className="line-clamp-2 flex-1 text-[13px] font-semibold leading-snug text-[var(--n-ink)]" data-testid="linked-note-title">{l.title}</span>
                <ExternalLink size={13} className="shrink-0 text-[var(--n-muted-2)] opacity-0 transition-opacity group-hover/lnk:opacity-100" />
              </div>
              <div className="flex items-center gap-1 text-[11px] text-[var(--n-muted)]">
                <FolderClosed size={11} className="shrink-0" />
                <span className="truncate">{l.folder_name || 'Unfiled'}</span>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-[var(--n-muted)]">
                <User size={11} className="shrink-0" />
                <span className="truncate">{l.creator_name || 'Unknown'}</span>
              </div>
              {l.can_unlink && (
                <button type="button" data-testid="linked-note-unlink" title="Unlink this note"
                  onClick={(e) => { e.stopPropagation(); unlink(l.id); }}
                  className="n-focus absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-md text-[var(--n-muted-2)] opacity-0 transition-all hover:bg-[var(--n-danger)]/10 hover:text-[var(--n-danger)] group-hover/lnk:opacity-100">
                  <Unlink size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <NotePicker taskId={taskId} onClose={() => setPickerOpen(false)}
          onLinked={() => { setPickerOpen(false); fetchLinks(); }} notify={notify} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Note search + multi-select dialog
// ---------------------------------------------------------------------------
function NotePicker({ taskId, onClose, onLinked, notify }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const debRef = useRef(null);

  const fetchResults = useCallback(async (term) => {
    setLoading(true);
    try {
      const { data } = await notesApi.get(`/notes/tasks/tasks/${taskId}/notes-picker`, { params: { q: term || '' } });
      setResults(data.notes || []);
    } catch { setResults([]); } finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { fetchResults(''); }, [fetchResults]);
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => fetchResults(q), 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q, fetchResults]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const submit = useCallback(async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      await notesApi.post(`/notes/tasks/tasks/${taskId}/links`, { note_ids: Array.from(selected) });
      notify?.(`Linked ${selected.size} note${selected.size > 1 ? 's' : ''}`);
      onLinked?.();
    } catch {
      notify?.('Could not link the selected notes. Please try again.');
    } finally { setSubmitting(false); }
  }, [selected, taskId, notify, onLinked]);

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="note-picker-dialog">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }} onClick={onClose} />
      <div className="relative flex max-h-[80vh] w-full max-w-[440px] flex-col overflow-hidden rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] shadow-[var(--n-shadow)]">
        <div className="flex items-center justify-between border-b border-[var(--n-line)] px-4 py-3">
          <h3 className="flex items-center gap-2 text-[14px] font-semibold text-[var(--n-ink)]"><NotebookText size={16} className="text-[var(--n-accent)]" /> Link a Note</h3>
          <button type="button" onClick={onClose} data-testid="note-picker-close" className="n-focus grid h-7 w-7 place-items-center rounded-lg text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={16} /></button>
        </div>
        <div className="border-b border-[var(--n-line)] px-4 py-2.5">
          <div className="flex items-center gap-2 rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas)] px-2.5 py-1.5">
            <Search size={14} className="text-[var(--n-muted-2)]" />
            <input data-testid="note-picker-search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
              placeholder="Search notes by title…" className="w-full bg-transparent text-[13px] text-[var(--n-ink)] outline-none placeholder:text-[var(--n-muted-2)]" />
          </div>
        </div>
        <div className="min-h-[160px] flex-1 overflow-y-auto n-scroll px-2 py-2">
          {loading ? (
            <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-[var(--n-muted)]"><Loader2 size={13} className="animate-spin" /> Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-2 py-6 text-center text-[12px] text-[var(--n-muted)]">No notes found{q ? ` for "${q}"` : ''}.</div>
          ) : (
            <ul className="space-y-1">
              {results.map((n) => {
                const on = selected.has(n.id);
                return (
                  <li key={n.id}>
                    <button type="button" data-testid="note-picker-item" data-selected={on ? 'true' : 'false'} onClick={() => toggle(n.id)}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${on ? 'bg-[var(--n-accent-bg)]' : 'hover:bg-[var(--n-canvas-2)]'}`}>
                      <span className={`grid h-4.5 w-4.5 shrink-0 place-items-center rounded border ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-border)]'}`} style={{ height: 18, width: 18 }}>
                        {on && <Check size={12} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-[var(--n-ink)]">{n.title}</span>
                        <span className="flex items-center gap-1.5 text-[11px] text-[var(--n-muted)]">
                          <FolderClosed size={10} />{n.folder_name || 'Unfiled'}
                          <span className="text-[var(--n-muted-2)]">·</span>
                          <User size={10} />{n.creator_name || 'Unknown'}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-[var(--n-line)] px-4 py-3">
          <span className="text-[12px] text-[var(--n-muted)]" data-testid="note-picker-selected-count">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-[12.5px] font-semibold text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">Cancel</button>
            <button type="button" data-testid="note-picker-link-submit" onClick={submit} disabled={selected.size === 0 || submitting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-white transition-opacity disabled:opacity-50">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Link{selected.size > 0 ? ` ${selected.size}` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
